/**
 * Observability config reader.
 *
 * Mirrors the backend gating: the frontend Sentry SDK initializes only when
 * BOTH the observability module flag is on AND a DSN is configured. All values
 * come from VITE_-prefixed env vars via the typed env module.
 *
 * Never read import.meta.env directly elsewhere — import from here.
 */

/** True when the observability module is enabled for the frontend build. */
export const OBSERVABILITY_ENABLED: boolean = boolEnv('VITE_MODULE_OBSERVABILITY');

/** Sentry DSN for the browser SDK. Empty string disables init. */
export const SENTRY_DSN: string = import.meta.env.VITE_SENTRY_DSN ?? '';

/** Environment tag attached to Sentry events (e.g. "production"). */
export const SENTRY_ENVIRONMENT: string =
  import.meta.env.VITE_SENTRY_ENVIRONMENT ?? import.meta.env.MODE ?? 'development';

/** Optional release identifier for tagging events / source maps. */
export const SENTRY_RELEASE: string = import.meta.env.VITE_SENTRY_RELEASE ?? '';

/** Performance trace sample rate (0.0–1.0). Defaults to 0 (off). */
export const SENTRY_TRACES_SAMPLE_RATE: number = numEnv('VITE_SENTRY_TRACES_SAMPLE_RATE', 0);

/**
 * Whether Sentry should initialize at all: the module must be enabled AND a DSN
 * must be present. Used by `initObservability()` and exported for tests.
 */
export function shouldInitSentry(): boolean {
  return OBSERVABILITY_ENABLED && SENTRY_DSN.length > 0;
}

// ---------------------------------------------------------------------------
// Local env helpers (kept local so this module is self-contained / testable).
// ---------------------------------------------------------------------------

function boolEnv(key: string, defaultValue = false): boolean {
  const raw = import.meta.env[key];
  if (raw === undefined || raw === '') return defaultValue;
  return raw === 'true' || raw === '1';
}

function numEnv(key: string, defaultValue: number): number {
  const raw = import.meta.env[key];
  if (raw === undefined || raw === '') return defaultValue;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : defaultValue;
}

export const observabilityConfig = {
  OBSERVABILITY_ENABLED,
  SENTRY_DSN,
  SENTRY_ENVIRONMENT,
  SENTRY_RELEASE,
  SENTRY_TRACES_SAMPLE_RATE,
} as const;
